import { Button } from 'components/Phantom/Button'
import styles from './QuizModal.module.scss'
import { Type } from 'components/Type'
import React, { FC, useState, useEffect, useRef } from 'react'
import { Icon } from 'components/Phantom/Icon'
import { Logo } from 'components/Logo'
import QuizCompleteScreen from './QuizCompleteScreen'
import QuizSplashScreen from './QuizSplashScreen'
import cx from 'classnames'
import { BottomLines, LeftLines, TopLines } from './Lines'

export type SleepScoreAnalysis = 'Low' | 'Moderate' | 'High' | 'Optimal'

export type QuizAnswer = {
	id: string
	label: string
	nextQuestionID?: string
	analysis?: SleepScoreAnalysis
}

type QuestionID = (typeof quizQuestions)[number]['id']

export type QuestionAndAnswer = {
	question: string
	questionID: QuestionID
} & QuizAnswer

export type AllQuizAnswers = Record<
	QuestionID,
	{
		question: string
		questionIndex: number
		answer: string
		answerID: string
		analysis?: SleepScoreAnalysis
	}
>

export type QuizQuestionRenderProps = {
	question?: string
	questionID: string
	subtext?: string
	answers?: QuizAnswer[]
	inputStates: QuizInputStates
	progress: number
	quizAnswers: AllQuizAnswers
	isLoading: boolean
	questionClassname?: string
	renderChildren?: (props: QuizQuestionRenderProps) => React.ReactNode
	handleInputChange: (
		inputName: keyof QuizInputStates,
		event: React.ChangeEvent<HTMLInputElement>
	) => void
	handleQuestionAnswer: (props: QuestionAndAnswer) => void
	handleNextQuestion: () => void
	handleClose: () => void
	handlePreviousQuestion: () => void
	handleEmailSubmit: (event: React.FormEvent<HTMLFormElement>) => Promise<void>
	handleQuizComplete: (props: { sleepPersona: string }) => void
}

export type QuizInputStates = {
	firstName?: string
	partnerName?: string
	email?: string
}

export const useQuizInputStates = ({
	email,
}: {
	email?: string
}): {
	inputStates: QuizInputStates
	setInputStates: React.Dispatch<React.SetStateAction<QuizInputStates>>
} => {
	const [inputStates, setInputStates] = useState<QuizInputStates>({
		firstName: '',
		partnerName: '',
		email: email ?? '',
	})

	return { inputStates, setInputStates }
}

const QuizHeader: React.FC<{
	progress: number
	handlePreviousQuestion?: () => void
	handleClose?: () => void
}> = ({ progress, handlePreviousQuestion, handleClose }) => {
	return (
		<div className={styles.quiz_header}>
			<div className={styles.quiz_header_top}>
				<div>
					<Button.Empty
						id="sleep-quiz-back"
						type="button"
						aria-label="Back button"
						onClick={handlePreviousQuestion}
						className={styles.header_button}>
						<Icon name="ArrowLeftLight" />
					</Button.Empty>
				</div>
				<div className={styles.desktop_logo_wrapper}>
					<Logo className={styles.header_logo} />
				</div>
				<div className={cx(styles.progress_bar_track, styles.mobile_progress_bar_track)}>
					<div
						className={styles.progress_bar}
						style={{ width: `${progress}%` }}
					/>
				</div>
				<div>
					<Button.Empty
						id="sleep-quiz-close"
						type="button"
						aria-label="Close modal button"
						onClick={handleClose}
						className={styles.header_button}>
						<Icon name="CloseLight" />
					</Button.Empty>
				</div>
			</div>
			<div className={cx(styles.progress_bar_track, styles.desktop_progress_bar_track)}>
				<div
					className={styles.progress_bar}
					style={{ width: `${progress}%` }}
				/>
			</div>
		</div>
	)
}

export const Question: FC<QuizQuestionRenderProps> = (props) => {
	const {
		question,
		questionID,
		subtext,
		answers,
		progress,
		handleQuestionAnswer,
		handlePreviousQuestion,
		handleClose,
		questionClassname,
		renderChildren,
	} = props
	const [gradientAngle, setGradientAngle] = useState(139)
	const containerRef = useRef<HTMLDivElement>(null)
	const animationFrameRef = useRef<number | null>(null)
	const currentAngleRef = useRef(139)

	useEffect(() => {
		// Calculate target angle based on progress (139 + progress * 4.7)
		const targetAngle = 139 + progress * 4.7

		const animate = (currentAngle: number): void => {
			// Smoothly interpolate towards target angle
			const diff = targetAngle - currentAngle
			if (Math.abs(diff) < 0.1) {
				setGradientAngle(targetAngle)
				currentAngleRef.current = targetAngle
				return
			}

			const nextAngle = currentAngle + diff * 0.01 // Adjust 0.01 for different animation speeds
			currentAngleRef.current = nextAngle
			setGradientAngle(nextAngle)
			animationFrameRef.current = requestAnimationFrame(() => animate(nextAngle))
		}

		animate(currentAngleRef.current)

		return () => {
			if (animationFrameRef.current !== null) {
				cancelAnimationFrame(animationFrameRef.current)
			}
		}
	}, [progress]) // Only re-run when progress changes

	return (
		<div
			ref={containerRef}
			className={styles.quiz_question_container}
			style={{
				background: `linear-gradient(${gradientAngle}deg, #122b67 4.86%, #000 72.43%)`,
			}}>
			<QuizHeader
				progress={progress}
				handlePreviousQuestion={handlePreviousQuestion}
				handleClose={handleClose}
			/>
			<div className={styles.quiz_content_body_container}>
				<div className={styles.left_lines_wrapper}>
					<LeftLines />
				</div>
				<div className={styles.bottom_lines_wrapper}>
					<BottomLines />
				</div>
				<div className={styles.top_lines_wrapper}>
					<TopLines />
				</div>
				<div className={styles.quiz_content_body}>
					<Type.Headline2 className={cx(styles.headline, questionClassname ?? '')}>
						{question}
					</Type.Headline2>
					<Type.Body1 className={styles.headchapter}>{subtext}</Type.Body1>
					<div className={styles.quiz_content_wrapper}>
						{answers && (
							<div className={styles.quiz_answers}>
								{answers.map((data) => (
									<Button.White
										key={`sleep-quiz-question-${data.id}}`}
										id={`sleep-quiz-question-${data.id}`}
										onClick={() => {
											handleQuestionAnswer?.({
												question: question ?? '',
												questionID,
												...data,
											})
										}}
										className={styles.quiz_button}>
										{data.label}
									</Button.White>
								))}
							</div>
						)}
						{renderChildren && (
							<div className={styles.quiz_button_container}>{renderChildren(props)}</div>
						)}
					</div>
				</div>
			</div>
		</div>
	)
}

const FirstNameForm: FC<QuizQuestionRenderProps> = ({
	question,
	handleQuestionAnswer,
	handleInputChange,
	inputStates,
	questionID,
}: QuizQuestionRenderProps) => {
	return (
		<form
			onSubmit={(e) => {
				e.preventDefault()
				handleQuestionAnswer({
					question: question ?? '',
					questionID,
					label: inputStates.firstName ?? '',
					id: questionID,
				})
			}}
			className={styles.quiz_form}>
			<input
				// eslint-disable-next-line jsx-a11y/no-autofocus
				autoFocus
				required
				type="text"
				placeholder="First name*"
				onChange={(event) => handleInputChange('firstName', event)}
				aria-label="Your first name input"
				autoComplete="given-name"
				value={inputStates.firstName ?? ''}
				className={styles.email_form_input}
			/>
			<Button.White
				type="submit"
				id="sleep-quiz-name-submit"
				className={styles.quiz_button}>
				Continue
			</Button.White>
		</form>
	)
}

const EmailForm: FC<QuizQuestionRenderProps> = ({
	handleInputChange,
	handleEmailSubmit,
	inputStates,
	isLoading,
}: QuizQuestionRenderProps) => {
	return (
		<form
			onSubmit={(e) => {
				void handleEmailSubmit(e)
			}}
			className={styles.quiz_form}>
			<input
				// eslint-disable-next-line jsx-a11y/no-autofocus
				autoFocus
				required
				type="email"
				placeholder="Email*"
				onChange={(event) => handleInputChange('email', event)}
				aria-label="Your email input"
				autoComplete="email"
				value={inputStates.email ?? ''}
				className={styles.email_form_input}
			/>
			<Button.White
				type="submit"
				id="sleep-quiz-email-submit"
				className={styles.quiz_button}>
				{isLoading ? 'Loading...' : 'Show my results'}
			</Button.White>
		</form>
	)
}

export const getSleepAnalysis = (
	quizAnswers: AllQuizAnswers
): {
	sleepPattern?: SleepScoreAnalysis
	timeToFallAsleep?: SleepScoreAnalysis
	sleepDuration?: SleepScoreAnalysis
} => {
	const sleepPattern = quizAnswers['sleepPattern']?.analysis
	const timeToFallAsleep = quizAnswers['timeToFallAsleep']?.analysis
	const sleepDuration = quizAnswers['sleepDuration']?.analysis

	return {
		sleepPattern,
		timeToFallAsleep,
		sleepDuration,
	}
}

type QuestionConfig = {
	component: React.ComponentType<QuizQuestionRenderProps>
	question?: string
	subtext?: string
	questionClassname?: string
	answers?: QuizAnswer[]
	renderChildren?: (props: QuizQuestionRenderProps) => React.ReactNode
}

export const quizQuestions: {
	id: string
	config: QuestionConfig
}[] = [
	{
		id: 'splash',
		config: {
			component: QuizSplashScreen,
		},
	},
	{
		id: 'name',
		config: {
			question: "Let's personalize your insights. What is your first name?",
			component: Question,
			renderChildren: (props: QuizQuestionRenderProps) => {
				return <FirstNameForm {...props} />
			},
		},
	},
	{
		id: 'sleepPattern',
		config: {
			question: 'How would you describe your typical sleep pattern?',
			answers: [
				{
					id: 'sleepPatternWithoutInterruption',
					label: 'Without interruptions',
					analysis: 'Optimal',
				},
				{
					id: 'sleepPatternWakeUp',
					label: 'Wake up a few times',
					analysis: 'Moderate',
				},
				{
					id: 'sleepPatternStruggleToStayAsleep',
					label: 'Struggle to stay asleep',
					analysis: 'Low',
				},
			],
			component: Question,
		},
	},
	{
		id: 'sleepDuration',
		config: {
			question: 'How long do you typically sleep?',
			answers: [
				{
					id: 'sleepDurationLessThan7Hours',
					label: 'Less than 7 hours',
					analysis: 'Low',
				},
				{
					id: 'sleepDuration7To8Hours',
					label: '7-8 hours',
					analysis: 'Moderate',
				},
				{
					id: 'sleepDurationMoreThan8Hours',
					label: 'More than 8 hours',
					analysis: 'Optimal',
				},
			],
			component: Question,
		},
	},
	{
		id: 'timeToFallAsleep',
		config: {
			question: 'How long does it take you to fall asleep?',
			answers: [
				{
					id: 'timeToFallAsleepLessThan10Minutes',
					label: 'Less than 10 minutes',
					analysis: 'Optimal',
				},
				{
					id: 'timeToFallAsleep10To30Minutes',
					label: '10-30 minutes',
					analysis: 'High',
				},
				{
					id: 'timeToFallAsleepMoreThan30Minutes',
					label: 'More than 30 minutes',
					analysis: 'Moderate',
				},
			],
			component: Question,
		},
	},
	{
		id: 'sleepQuality',
		config: {
			question: 'How do you usually feel when you wake up?',
			answers: [
				{
					label: 'Refreshed',
					id: 'sleepQualityRefreshed',
				},
				{
					label: 'A bit groggy',
					id: 'sleepQualityGroggy',
				},
				{
					label: 'Exhausted',
					id: 'sleepQualityExhausted',
				},
			],
			component: Question,
		},
	},
	{
		id: 'snoring',
		config: {
			question: 'Is snoring a problem in your bedroom?',
			answers: [
				{
					label: 'Yes, I snore',
					id: 'snoringYesI',
				},
				{
					label: 'Yes, my partner snores',
					id: 'snoringYesMyPartner',
				},
				{
					label: 'Snoring is not a problem',
					id: 'snoringNotAProblem',
				},
			],
			component: Question,
		},
	},
	{
		id: 'sleepTemperature',
		config: {
			question: "What's your ideal sleeping temperature?",
			answers: [
				{
					label: 'Cool and crisp',
					id: 'sleepTemperatureCoolAndCrisp',
				},
				{
					label: 'Warm and cozy',
					id: 'sleepTemperatureWarmAndCozy',
				},
				{
					label: 'It depends on the season',
					id: 'sleepTemperatureItDependsOnTheSeason',
				},
			],
			component: Question,
		},
	},
	{
		id: 'sleepMetrics',
		config: {
			question: 'Are you curious about how your sleep patterns measure against optimal levels?',
			answers: [
				{
					label: 'Yes, very interested',
					id: 'sleepMetricsYesVeryInterested',
				},
				{
					label: 'Somewhat interested',
					id: 'sleepMetricsSomewhatInterested',
				},
				{
					label: 'Not interested at all',
					id: 'sleepMetricsNotInterestedAtAll',
				},
			],
			component: Question,
		},
	},
	{
		id: 'email',
		config: {
			question: 'Your results are almost ready',
			subtext: 'Let us know where to send them',
			questionClassname: styles.quiz_question_email,
			component: Question,
			renderChildren: (props: QuizQuestionRenderProps) => {
				return <EmailForm {...props} />
			},
		},
	},
	{
		id: 'complete',
		config: {
			component: QuizCompleteScreen,
		},
	},
]
